import { Box, CircularProgress } from '@mui/material'
import { useEffect, useState } from 'react'
import { useMutation } from 'react-query'
import { isAdmin } from '../../../domain/Account/isAdmin'
import { backendClient } from '../../../interactors/clients/client'
import { CustomersClient } from '../../../interactors/clients/CustomerClient'
import { CustomerDto } from '../../../interactors/gen/backendClient'
import { useAccountsStore } from '../../../store/AccountsStore'
import { useGlobalSnackbarStore } from '../../../store/GlobalSnackBarStore'
import { theme } from '../../theme'
import OfferTabs from './OfferTabs'
import { TemporaryOffersDraft } from './TemporaryOffersDraft'

interface RenewSubscriptionData {
  stripeCustomerId: string
}

interface PortalSessionData {
  customerId: string
}

export const FunctionalitiesTab = () => {
  /* const accountsStore = useAccountsStore()
  const membersCount = accountsStore.members.filter(
    (member) => member.permission !== AccountDtoPermission.Silent
  ).length */
  const globalSnackbarStore = useGlobalSnackbarStore()
  const queryParams = new URLSearchParams(window.location.search)
  const [customer, setCustomer] = useState<CustomerDto | null>(null)

  const accountsStore = useAccountsStore()
  const currentUserIsAdmin = accountsStore.connectedAccount ? isAdmin(accountsStore.connectedAccount) : false
  const renewMutation = useMutation(
    (data: RenewSubscriptionData) => backendClient.post('/stripe/reactivate-subscription', data),
    {
      onSuccess: () => {
        globalSnackbarStore.triggerSuccessMessage('Votre abonnement a bien été renouvelé')
      },
      onError: (error) => {
        globalSnackbarStore.triggerErrorMessage("Impossible de renouveler l'abonnement pour le moment.")
        console.error('Error Creating Checkout Session:', error)
      },
    }
  )

  const dashboardMutation = useMutation(
    (data: PortalSessionData) => backendClient.post('/stripe/create-portal-session-customer-id', data),
    {
      onSuccess: (data) => {
        window.location.href = data.data
      },
      onError: (error) => {
        globalSnackbarStore.triggerErrorMessage("Impossible d'ouvrir la gestion de l'abonnement pour le moment.")
        console.error('Error Creating Portal Session:', error)
      },
    }
  )

  const handleClick = async () => {
    const stripeCustomerId = customer?.stripeCustomerId?.trim() ?? ''

    if ((hasCanceledSubscription || isSubscribed) && !stripeCustomerId) {
      globalSnackbarStore.triggerErrorMessage(
        "Impossible d'ouvrir la gestion de l'abonnement pour ce compte pour le moment."
      )
      return
    }

    if (hasCanceledSubscription && stripeCustomerId) {
      try {
        const { data: session } = await renewMutation.mutateAsync({ stripeCustomerId })
        console.log('Subscription renewed:', session)
      } catch (error) {
        // Handle error
        console.error('Error Renewing Subscription:', error)
      }
    } else if (isSubscribed) {
      try {
        const { data: session } = await dashboardMutation.mutateAsync({ customerId: stripeCustomerId })
        console.log('Portal Session Created:', session)
      } catch (error) {
        // Handle error
        console.error('Error Creating Portal Session:', error)
      }
    }
  }

  const isSubscribed = customer?.subscription?.status === 'active'

  const hasCanceledSubscription = customer?.subscription?.status === 'canceled'

  const fetchCustomerMutation = useMutation(async () => await CustomersClient.getCurrentCustomer(), {
    onSuccess: (data) => {
      setCustomer(data || null)
    },
  })

  useEffect(() => {
    fetchCustomerMutation.mutate()
  }, [])

  useEffect(() => {
    if (queryParams.get('success')) {
      globalSnackbarStore.triggerSuccessMessage('Félicitations ! Vous êtes maintenant abonné à Petso')
    } else if (queryParams.get('canceled')) {
      globalSnackbarStore.triggerErrorMessage('Votre paiement a échoué. Veuillez rééssayer.')
    }
  }, [])

  if (!customer) {
    return <CircularProgress />
  }

  return (
    <>
      <Box sx={{ padding: 2, backgroundColor: 'grey.100', minHeight: '100%' }}>
        <Box sx={{ maxWidth: '1200px', marginX: 'auto', marginY: 2 }}>
          <Box
            sx={{
              backgroundColor: theme.palette.secondary.main,
              color: 'white',
              fontSize: '22px',
              fontWeight: 700,
              width: 'fit-content',
              p: 1,
              py: 0.5,
              pl: 2,
              pr: 10,
              mb: 4,
              borderRadius: '11px',
              transform: 'rotate(-1deg)',
            }}
          >
            Abonnement
          </Box>
          <Box sx={{ maxWidth: '1050px', marginX: 'auto' }}>
            {/* Draft temporaire de la nouvelle présentation des offres */}
            <TemporaryOffersDraft
              customer={customer}
              isSubscribed={isSubscribed}
              isManageLoading={renewMutation.isLoading || dashboardMutation.isLoading}
              currentUserIsAdmin={currentUserIsAdmin}
              onManageSubscription={handleClick}
            />
            {!customer.stripeAccountId && <OfferTabs />}
          </Box>
        </Box>
      </Box>
    </>
  )
}
